import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { MicroSession, Narrative } from '@/types/micro';
import { MicroLessonLayout } from './MicroLessonLayout';
import { microSessionService } from '@/services/microSessionService';
import { progressTrackingService } from '@/services/progressTrackingService';
import { LessonContent } from './LessonContent';
import { LearningObjectiveCard } from './LearningObjectiveCard';
import { getNarrativeBridge } from '@/data/lessonNarrativeBridges';
import { usePersonalizedPath } from '@/hooks/usePersonalizedPath';
import { Link } from 'react-router-dom';
import { advancePathIndex } from '@/services/userPathService';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

interface MicroLessonWrapperProps {
  micro: MicroSession;
}

export const MicroLessonWrapper: React.FC<MicroLessonWrapperProps> = ({ micro }) => {
  const [narrativeRead, setNarrativeRead] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const { path, refetch } = usePersonalizedPath();
  const { user } = useAuth();

  // Check if lesson is already completed on mount
  useEffect(() => {
    const checkCompletion = async () => {
      if (!user) return;
      try {
        const progress = await progressTrackingService.getProgress(micro.id);
        // Check both status AND completedAt for robustness
        if (progress?.status === 'completed' || progress?.completedAt) {
          setIsCompleted(true);
        }
      } catch (error) {
        console.warn('[MicroLessonWrapper] Failed to check completion status', error);
      }
    };
    checkCompletion();
  }, [micro.id, user]);

  // Change default to expanded (true) for all sections
  const [narrativeExpanded, setNarrativeExpanded] = useState<Record<keyof Narrative, boolean>>({
    problem: true,
    what: true,
    why: true,
    how: true,
  });

  const handleNarrativeRead = useCallback(async () => {
    // 1. Start Fade Out (Cinema Curtain closes)
    setIsTransitioning(true);

    // Wait for fade to complete (500ms)
    await new Promise(resolve => setTimeout(resolve, 500));

    // 2. Perform State Switch & Logic while hidden
    await microSessionService.startSession(micro.id);
    setNarrativeRead(true);

    // Wait a tiny bit to ensure render cycle catches up? 
    // Usually not needed but feels smoother.
    await new Promise(resolve => setTimeout(resolve, 100));

    // 3. Start Fade In (Cinema Curtain opens)
    setIsTransitioning(false);
  }, [micro.id]);

  const toggleSection = (section: keyof Narrative) => {
    setNarrativeExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const narrativeBridge = getNarrativeBridge(micro.id);

  // Calculate next lesson from personalized path
  const nextLesson = useMemo(() => {
    if (!path || !path.path) return null;
    const currentIndex = path.path.indexOf(micro.id);
    if (currentIndex === -1 || currentIndex >= path.path.length - 1) return null;
    const nextLessonId = path.path[currentIndex + 1];
    return microSessionService.getSessionById(nextLessonId);
  }, [path, micro.id]);

  const handleLessonComplete = useCallback(async () => {
    const userId = user?.id || path?.user_id;
    await advancePathIndex(micro.id, userId);
    await refetch();
  }, [micro.id, path?.user_id, user?.id, refetch]);

  const practiceButtonLabel = useMemo(() => {
    // Show completed state if lesson is already done
    if (isCompleted) {
      return "✓ Completed — Practice again";
    }

    const options = [
      "Let's get to work",
      "Dive in",
      "Start building",
      "Give it a try",
      "Let's go",
      "Start the session",
      "Jump in",
      "Begin practice",
      "Start your win",
      "Let's do this"
    ];
    // Simple hash of string to get consistent index
    const hash = micro.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return options[hash % options.length];
  }, [micro.id, isCompleted]);

  const renderContent = () => {
    if (narrativeRead) {
      return (
        <MicroLessonLayout
          sessionId={micro.id}
          personaLabel="Lyra"
          title={micro.title}
          displayTitle={micro.displayTitle}
          tagline={micro.subTitle || ''}
          steps={micro.steps}
          aiPrompt={micro.aiPrompt}
          inputPlaceholder="Share your thoughts on this lesson..."
          introPrompt={micro.introPrompt}
          quickPrompts={micro.quickPrompts}
          narrativeBridge={narrativeBridge}
          learningMode={micro.learningMode}
          nextLessonId={nextLesson?.id}
          nextLessonTitle={nextLesson?.title}
          skillArea={micro.skillArea}
          onComplete={handleLessonComplete}
          chatIntro={(micro as any)._chatIntro}
        >
          <LessonContent microId={micro.id} />
        </MicroLessonLayout>
      );
    }

    const narrative = micro.narrative;
    if (!narrative) {
      // Fallback if no narrative (Legacy behavior, but wrapped for consistency)
      return (
        <MicroLessonLayout
          sessionId={micro.id}
          personaLabel="Maya"
          title={micro.title}
          displayTitle={micro.displayTitle}
          tagline={micro.tagline || ''}
          steps={micro.steps}
          aiPrompt={micro.aiPrompt}
          inputPlaceholder="Share your thoughts on this lesson..."
          introPrompt={micro.introPrompt}
          quickPrompts={micro.quickPrompts}
          narrativeBridge={narrativeBridge}
          learningMode={micro.learningMode}
          nextLessonId={nextLesson?.id}
          nextLessonTitle={nextLesson?.title}
          skillArea={micro.skillArea}
          onComplete={handleLessonComplete}
          chatIntro={(micro as any)._chatIntro}
        >
          <LessonContent microId={micro.id} />
        </MicroLessonLayout>
      );
    }

    // Default Narrative View
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Link
            to="/micro-dashboard"
            className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-2"
          >
            ← Back to Micro Hub
          </Link>
        </div>

        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{micro.title}</h1>

        </div>

        {/* Learning Objective Card with Practice Button */}
        <LearningObjectiveCard
          sessionId={micro.id}
          sessionTitle={micro.title}
          skillArea={micro.skillArea}
          narrative={narrative}
          narrativeBridge={narrativeBridge}
        />

        {/* Practice Button */}
        <div className="pt-2 flex justify-center">
          <Button
            onClick={handleNarrativeRead}
            className={`w-full max-w-md text-base h-12 rounded-[24px] transition-all ${
              isCompleted
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-slate-900 hover:bg-slate-800 text-white'
            }`}
          >
            {practiceButtonLabel}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Cinema Curtain Overlay */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="fixed inset-0 z-[100] bg-[#09090b]" // Use the same Zinc black as Focus Mode
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Main Content */}
      {renderContent()}
    </>
  );
};
